import * as React from "react";
import {
  FirestoreError,
  initializeApp,
  initializeFirestore,
  getAuth,
  Firestore,
  FirestoreErrorCode,
} from "@atgof-firebase/firebase";
import {
  countryCodes,
  initializeUI,
  recaptchaVerification,
  requireDisplayName,
} from "@firebase-oss/ui-core";
import "@firebase-oss/ui-styles/dist.min.css";
import { FirebaseUIProvider } from "@firebase-oss/ui-react";
import * as WebBrowser from "expo-web-browser";
import { useNetInfo } from "@react-native-community/netinfo";
import useCachedResources from "./hooks/useCachedResources";
import Navigation from "./navigation";
import { MembershipResults } from "./common/membership";
import LoadingScreen from "./components/LoadingScreen";
import ErrorScreen from "./components/ErrorScreen";
import { ProjectState, projectStateFor } from "./data/project";
import { onBackendConnected } from "./data/backend";
import ErrorBoundary from "./components/ErrorBoundary";
import * as Phrases from "./common/phrases";
import { LanguageContext } from "./common/language";
import dayjs from "dayjs";
import {
  Platform,
  SafeAreaView,
  Text as RawText,
  View,
  useColorScheme,
} from "react-native";
import { retrieveItem, storeItem } from "./data/prefs";
import {
  adaptNavigationTheme,
  MD3DarkTheme,
  MD3LightTheme,
  PaperProvider,
  Text,
  useTheme,
} from "react-native-paper";
import {
  DarkTheme as NavigationDarkTheme,
  DefaultTheme as NavigationDefaultTheme,
  Theme,
} from "@react-navigation/native";
import * as SplashScreen from "expo-splash-screen";
import { useFonts } from "expo-font";
import { RobotoCondensed_400Regular } from "@expo-google-fonts/roboto-condensed";
import { enableScreens } from "react-native-screens";
import { configureFirebase, shouldUsePersistence } from "./config";
import { appConfig } from "./config/firebase";
import { UserContext } from "./data/userContext";
import { ProjectContext } from "./data/projectContext";
import { ProjectsContext } from "./data/projectsContext";
import {
  CoreComponentClass,
  CoreComponentsContext,
} from "./common/core-components";
import { NewItemIndicator } from "./components/NewItemIndicator";
import { TimeOfDayRepresentation } from "./components/TimeOfDayRepresentation";
import SignInScreen from "./screens/SignInScreen";
import { useUserState } from "./data/user";

enableScreens();

SplashScreen.preventAutoHideAsync();

const app = initializeApp(appConfig);

const dbPromise = initializeFirestore(app, shouldUsePersistence);
const auth = getAuth();

configureFirebase(auth, dbPromise);

WebBrowser.maybeCompleteAuthSession();

const kCurrentProjectId = "currentProjectId";

function App_({ theme }: { theme: Theme }) {
  const isLoadingComplete = useCachedResources();

  const [db, setDb] = React.useState<Firestore>();
  React.useEffect(() => {
    dbPromise.then(setDb);
  }, [dbPromise]);

  const { language, setLanguage } = React.useContext(LanguageContext);
  const userState = useUserState(db);
  const { isInternetReachable } = useNetInfo();
  const [invitations, setInvitations] = React.useState(
    null as MembershipResults,
  );
  const [memberships, setMemberships] = React.useState(
    null as MembershipResults,
  );
  const projects = React.useMemo<ProjectState[]>(
    () =>
      !memberships || "error" in memberships || !memberships.docs.length
        ? []
        : memberships.docs.map(projectStateFor),
    [memberships],
  );

  const [currentProject, setCurrentProject] = React.useState(
    projectStateFor(undefined),
  );

  const [retrievedCurrentProjectId, setRetrievedCurrentProjectId] =
    React.useState<string | null>(null);

  React.useEffect(() => {
    retrieveItem(kCurrentProjectId).then(setRetrievedCurrentProjectId);
  }, []);
  React.useEffect(() => {
    const projectId = currentProject.project?.id;
    if (projectId) storeItem(kCurrentProjectId, projectId); // TODO .catch
  }, [currentProject]);

  React.useEffect(
    () =>
      setCurrentProject(
        (cur) =>
          (cur.project?.id &&
            projects.find(
              (candidate) => candidate.project?.id === cur.project?.id,
            )) ||
          (retrievedCurrentProjectId &&
            projects.find(
              (candidate) =>
                candidate.project?.id === retrievedCurrentProjectId,
            )) ||
          (projects.length ? projects[0] : projectStateFor(undefined)),
      ),
    [retrievedCurrentProjectId, projects],
  );

  function ph(key: string): Phrases.Phrase {
    return Phrases.ph(language, key);
  }

  function setUserLanguage(language: string) {
    setLanguage(language);
    userState?.user?.ref.set({ language }, { merge: true });
  }

  function errorHandler(
    setState: React.Dispatch<React.SetStateAction<MembershipResults>>,
    ignoreCodes: FirestoreErrorCode[] = [],
  ) {
    return function (error: FirestoreError) {
      setState(ignoreCodes.includes(error.code) ? { docs: [] } : { error });
    };
  }

  React.useEffect(() => {
    if (userState?.user && isInternetReachable)
      onBackendConnected(userState.user);
  }, [userState?.user?.id, isInternetReachable]);

  React.useEffect(() => {
    if (!userState?.user) return;
    const { authUser } = userState;
    const { emailVerified } = authUser;
    setMemberships(emailVerified ? null : { docs: [] });
    setInvitations(emailVerified ? null : { docs: [] });
    if (emailVerified && db) {
      const { email } = authUser;
      const invitationsUnsub =
        email &&
        db
          .collectionGroup("invitations")
          .where("email", "==", email.toLocaleLowerCase())
          .onSnapshot(
            ({ docs }) => setInvitations({ docs }),
            errorHandler(setInvitations, ["permission-denied"]),
          );
      const membershipsUnsub = db
        .collectionGroup("memberships")
        .where("user", "==", userState.user.ref)
        .where("deleted", "==", false)
        .onSnapshot(
          ({ docs }) => setMemberships({ docs }),
          errorHandler(setMemberships),
        );
      return () => {
        if (invitationsUnsub) invitationsUnsub();
        membershipsUnsub();
      };
    }
  }, [userState, db]);

  const { colors } = useTheme();
  if (!isLoadingComplete && db) return null;
  return (
    <CoreComponentsContext.Provider
      value={{
        View: View as CoreComponentClass,
        Text: Text as unknown as CoreComponentClass,
        NewItemIndicator,
        TimeOfDayRepresentation,
        RawText: RawText as CoreComponentClass,
        colors: {
          fixture: colors.secondary,
          onBackground: colors.onBackground,
        },
      }}
    >
      <LanguageContext.Provider
        value={{
          language: language,
          setLanguage: setUserLanguage,
          ph: ph,
        }}
      >
        {!userState ? (
          <SignInScreen />
        ) : !(userState.user && memberships) ? (
          <LoadingScreen />
        ) : (
          <UserContext.Provider value={{ ...userState, user: userState.user }}>
            {"error" in memberships ? (
              <ErrorScreen error={memberships as any} />
            ) : (
              <ProjectsContext.Provider value={{ projects, setCurrentProject }}>
                <ProjectContext.Provider value={currentProject}>
                  <Navigation
                    invitations={invitations}
                    projects={projects}
                    theme={theme}
                  />
                </ProjectContext.Provider>
              </ProjectsContext.Provider>
            )}
          </UserContext.Provider>
        )}
      </LanguageContext.Provider>
    </CoreComponentsContext.Provider>
  );
}

export default function App() {
  const [fontsLoaded] = useFonts({ RobotoCondensed_400Regular });
  const onLayoutRootView = React.useCallback(async () => {
    if (fontsLoaded) await SplashScreen.hideAsync();
  }, [fontsLoaded]);
  const [language, setLanguage_] = React.useState(Phrases.defaultLang);

  function ph(key: string): Phrases.Phrase {
    return Phrases.safePh(language, key);
  }

  function setLanguage(lang: string) {
    setLanguage_(lang);
    dayjs.locale(lang);
  }

  const { LightTheme, DarkTheme } = adaptNavigationTheme({
    reactNavigationLight: NavigationDefaultTheme,
    reactNavigationDark: NavigationDarkTheme,
  });

  const CombinedDefaultTheme = {
    ...MD3LightTheme,
    ...LightTheme,
    colors: {
      ...MD3LightTheme.colors,
      ...LightTheme.colors,
    },
  };
  const CombinedDarkTheme = {
    ...MD3DarkTheme,
    ...DarkTheme,
    colors: {
      ...MD3DarkTheme.colors,
      ...DarkTheme.colors,
    },
  };

  const colorScheme = useColorScheme();
  const theme =
    colorScheme === "dark" ? CombinedDarkTheme : CombinedDefaultTheme;

  const firebaseUI = React.useMemo(
    () =>
      initializeUI({
        app,
        behaviors: [
          countryCodes({
            allowedCountries: ["GB"],
            defaultCountry: "GB",
          }),
          requireDisplayName(),
          recaptchaVerification({
            size: "invisible",
            theme: colorScheme ?? "light",
          }),
        ],
      }),
    [app],
  );

  return (
    <FirebaseUIProvider ui={firebaseUI}>
      <LanguageContext.Provider
        value={{
          language: language,
          setLanguage: setLanguage,
          ph: ph,
        }}
      >
        <PaperProvider theme={theme}>
          <SafeAreaView
            style={{
              flex: 1,
              ...(Platform.OS === "web" ? { height: "100vh" } : {}),
            }}
            onLayout={onLayoutRootView}
          >
            <ErrorBoundary toplevel>
              <App_ theme={theme} />
            </ErrorBoundary>
          </SafeAreaView>
        </PaperProvider>
      </LanguageContext.Provider>
    </FirebaseUIProvider>
  );
}
